import { useTranslation } from "react-i18next"
import { useDateTranslations } from "../../hooks/use-date-translations"

export const useTranslations = (defaults = defaultTranslations): TreeGridTranslations => {
  const { t } = useTranslation()
  const { t: tTreeGrid } = useTranslation("treegrid")
  const dateTranslations = useDateTranslations()

  const longDayNames = dateTranslations.fullDayNames.join(",") || defaults.longDayNames
  const shortDayNames = dateTranslations.abbreviatedDayNames.join(",") || defaults.shortDayNames
  const day2CharNames = dateTranslations.shortDayNames.join(",") || defaults.day2CharNames
  const day1CharNames = dateTranslations.narrowDayNames.join(",") || defaults.day1CharNames

  const longMonthNames = dateTranslations.fullMonthNames.join(",") || defaults.longMonthNames
  const longMonthNames2 = longMonthNames
  const shortMonthNames = dateTranslations.shortMonthsNames.join(",") || defaults.shortMonthNames
  const shortWeekLabel = dateTranslations.shortWeekLabel || defaults.shortWeekLabel

  return {
    // Common translations from translation
    okButtonLabel: t("okButtonLabel") || defaults.okButtonLabel,
    cancelButtonLabel: t("cancel") || defaults.cancelButtonLabel,
    hideAllButtonLabel: t("hideAllButtonLabel") || defaults.hideAllButtonLabel,
    allOnButtonLabel: t("allOnButtonLabel") || defaults.allOnButtonLabel,
    showLabel: t("show"),
    linksColumnHeader: t("links") || defaults.linksColumnHeader,
    linksAndAttachmentsColumnHeader:
      tTreeGrid("linksAndAttachmentsColumnHeader") || defaults.linksAndAttachmentsColumnHeader,
    color: t("color") || defaults.color,
    // openColumnHeader: t("openButton") || defaults.openColumnHeader,
    openColumnHeader: "", // As per design, open column header should be empty
    Default: t("default") || defaults.Default,
    actionsColumnHeader: t("actionsColumnHeader") || defaults.actionsColumnHeader,
    showMenu: t("showMenu") || defaults.showMenu,
    // Status translations from project
    notStarted: t("notStarted") || defaults.notStarted,
    inProgress: t("inProgress") || defaults.inProgress,
    completed: t("completed") || defaults.completed,
    rejected: t("rejected") || defaults.rejected,
    // Date translations
    longDayNames,
    shortDayNames,
    day2CharNames,
    day1CharNames,
    longMonthNames,
    longMonthNames2,
    shortMonthNames,
    shortWeekLabel,
    // TreeGrid specific translations
    rowNumberColumnHeader: tTreeGrid("rowNumberColumnHeader") || defaults.rowNumberColumnHeader,
    taskNumberShortColumnHeader: tTreeGrid("taskNumberShortColumnHeader") || defaults.taskNumberShortColumnHeader,
    projectNumberShortColumnHeader:
      tTreeGrid("projectNumberShortColumnHeader") || defaults.projectNumberShortColumnHeader,
    titleColumnHeader: tTreeGrid("titleColumnHeader") || defaults.titleColumnHeader,
    descriptionColumnHeader: tTreeGrid("descriptionColumnHeader") || defaults.descriptionColumnHeader,
    dueDateColumnHeader: tTreeGrid("dueDateColumnHeader") || defaults.dueDateColumnHeader,
    customersColumnHeader: tTreeGrid("customersColumnHeader") || defaults.customersColumnHeader,
    workspacesColumnHeader: tTreeGrid("workspacesColumnHeader") || defaults.workspacesColumnHeader,
    responsibleColumnHeader: tTreeGrid("responsibleColumnHeader") || defaults.responsibleColumnHeader,
    completedColumnHeader: tTreeGrid("completedColumnHeader") || defaults.completedColumnHeader,
    toolbarSearchInputLabel: tTreeGrid("toolbarSearchInputLabel") || defaults.toolbarSearchInputLabel,
    toolbarSearchInputTip: tTreeGrid("toolbarSearchInputTip") || defaults.toolbarSearchInputTip,
    toolbarPrintTip: tTreeGrid("toolbarPrintTip") || defaults.toolbarPrintTip,
    toolbarExportTip: tTreeGrid("toolbarExportTip") || defaults.toolbarExportTip,
    toolbarExpandAllTip: tTreeGrid("toolbarExpandAllTip") || defaults.toolbarExpandAllTip,
    toolbarCollapseAllTip: tTreeGrid("toolbarCollapseAllTip") || defaults.toolbarCollapseAllTip,
    toolbarColumnsTip: tTreeGrid("toolbarColumnsTip") || defaults.toolbarColumnsTip,
    toolbarFilterTip: tTreeGrid("toolbarFilterTip") || defaults.toolbarFilterTip,
    toolbarTodoListSummaryText:
      tTreeGrid("toolbarTodoListSummaryText", {
        rowCount: "{{rowCount}}",
      }) || defaults.toolbarTodoListSummaryText,
    toolbarActivitiesListSummaryText:
      tTreeGrid("toolbarActivitiesListSummaryText", {
        rowCount: "{{rowCount}}",
      }) || defaults.toolbarActivitiesListSummaryText,
    toolbarProjectListSummaryText:
      tTreeGrid("toolbarProjectListSummaryText", {
        rowCount: "{{rowCount}}",
      }) || defaults.toolbarProjectListSummaryText,
    toolbarTaskListSummaryText:
      tTreeGrid("toolbarTaskListSummaryText", {
        rowCount: "{{rowCount}}",
      }) || defaults.toolbarTaskListSummaryText,
    pageHeaderRowLabel: tTreeGrid("pageHeaderRowLabel") || defaults.pageHeaderRowLabel,
    headerRowLabel: tTreeGrid("headerRowLabel") || defaults.headerRowLabel,
    statusFilterOffLabel: tTreeGrid("statusFilterOffLabel") || defaults.statusFilterOffLabel,
    managersFilterOffLabel: tTreeGrid("managersFilterOffLabel") || defaults.managersFilterOffLabel,
    participantsFilterOffLabel: tTreeGrid("participantsFilterOffLabel") || defaults.participantsFilterOffLabel,
    suppliersFilterOffLabel: tTreeGrid("suppliersFilterOffLabel") || defaults.suppliersFilterOffLabel,
    workspacesFilterOffLabel: tTreeGrid("workspacesFilterOffLabel") || defaults.workspacesFilterOffLabel,
    stateFilterOffLabel: tTreeGrid("stateFilterOffLabel") || defaults.stateFilterOffLabel,
    customersFilterOffLabel: tTreeGrid("customersFilterOffLabel") || defaults.customersFilterOffLabel,
    responsibleFilterOffLabel: tTreeGrid("responsibleFilterOffLabel") || defaults.responsibleFilterOffLabel,
    addTodoButtonLabel: tTreeGrid("addTodoButtonLabel") || defaults.addTodoButtonLabel,
    showTimeColumnHeader: tTreeGrid("showTimeColumnHeader") || defaults.showTimeColumnHeader,
    markTodoAsCompletedTip: tTreeGrid("markTodoAsCompletedTip") || defaults.markTodoAsCompletedTip,
    markTodoAsNotCompletedTip: tTreeGrid("markTodoAsNotCompletedTip") || defaults.markTodoAsNotCompletedTip,
    plannedStartDateColumnHeader: tTreeGrid("plannedStartDateColumnHeader") || defaults.plannedStartDateColumnHeader,
    plannedEndDateColumnHeader: tTreeGrid("plannedEndDateColumnHeader") || defaults.plannedEndDateColumnHeader,
    actualStartDateColumnHeader: tTreeGrid("actualStartDateColumnHeader") || defaults.actualStartDateColumnHeader,
    actualEndDateColumnHeader: tTreeGrid("actualEndDateColumnHeader") || defaults.actualEndDateColumnHeader,
    statusColumnHeader: tTreeGrid("statusColumnHeader") || defaults.statusColumnHeader,
    statusDescriptionColumnHeader: tTreeGrid("statusDescriptionColumnHeader") || defaults.statusDescriptionColumnHeader,
    stateColumnHeader: tTreeGrid("stateColumnHeader") || defaults.stateColumnHeader,
    participantsColumnHeader: tTreeGrid("participantsColumnHeader") || defaults.participantsColumnHeader,
    suppliersColumnHeader: tTreeGrid("suppliersColumnHeader") || defaults.suppliersColumnHeader,
    notReported: tTreeGrid("notReported") || defaults.notReported,
    managersColumnHeader: tTreeGrid("managersColumnHeader") || defaults.managersColumnHeader,
    rejectedDateColumnHeader: tTreeGrid("rejectedDateColumnHeader") || defaults.rejectedDateColumnHeader,
    createdAtColumnHeader: tTreeGrid("createdAtColumnHeader") || defaults.createdAtColumnHeader,
    creatorColumnHeader: tTreeGrid("creatorColumnHeader") || defaults.creatorColumnHeader,
    templateTitleColumnHeader: tTreeGrid("templateTitleColumnHeader") || defaults.templateTitleColumnHeader,
    completionBasedOnTasksColumnHeader:
      tTreeGrid("completionBasedOnTasksColumnHeader") || defaults.completionBasedOnTasksColumnHeader,
    completionColumnHeader: tTreeGrid("completionColumnHeader") || defaults.completionColumnHeader,
    activitiesListProjectGroupHeader:
      tTreeGrid("activitiesListProjectGroupHeader") || defaults.activitiesListProjectGroupHeader,
    activitiesListTaskTodoAndCalendarEventGroupHeader:
      tTreeGrid("activitiesListTaskTodoAndCalendarEventGroupHeader") ||
      defaults.activitiesListTaskTodoAndCalendarEventGroupHeader,
    addProjectButtonLabel: tTreeGrid("addProjectButtonLabel") || defaults.addProjectButtonLabel,
    zoomMenuHoursAndQuarters: tTreeGrid("zoomMenuHoursAndQuarters") || defaults.zoomMenuHoursAndQuarters,
    zoomMenuDaysAndHours: tTreeGrid("zoomMenuDaysAndHours") || defaults.zoomMenuDaysAndHours,
    zoomMenuWeeksAndHalfDays: tTreeGrid("zoomMenuWeeksAndHalfDays") || defaults.zoomMenuWeeksAndHalfDays,
    zoomMenuWeeksAndDays: tTreeGrid("zoomMenuWeeksAndDays") || defaults.zoomMenuWeeksAndDays,
    zoomMenuMonthsAndWeeks: tTreeGrid("zoomMenuMonthsAndWeeks") || defaults.zoomMenuMonthsAndWeeks,
    zoomMenuMonthsWeeksAndDays: tTreeGrid("zoomMenuMonthsWeeksAndDays") || defaults.zoomMenuMonthsWeeksAndDays,
    zoomMenuQuartersAndMonths: tTreeGrid("zoomMenuQuartersAndMonths") || defaults.zoomMenuQuartersAndMonths,
    zoomMenuHalvesAndMonths: tTreeGrid("zoomMenuHalvesAndMonths") || defaults.zoomMenuHalvesAndMonths,
    zoomMenuYearsAndQuarters: tTreeGrid("zoomMenuYearsAndQuarters") || defaults.zoomMenuYearsAndQuarters,
    taskNumberColumnHeader: tTreeGrid("taskNumberColumnHeader") || defaults.taskNumberColumnHeader,
    durationColumnHeader: tTreeGrid("durationColumnHeader") || defaults.durationColumnHeader,
    daysLeftColumnHeader: tTreeGrid("daysLeftColumnHeader") || defaults.daysLeftColumnHeader,
    basePlanStartDateColumnHeader: tTreeGrid("basePlanStartDateColumnHeader") || defaults.basePlanStartDateColumnHeader,
    basePlanEndDateColumnHeader: tTreeGrid("basePlanEndDateColumnHeader") || defaults.basePlanEndDateColumnHeader,
    ganttDependenciesColumnHeader: tTreeGrid("ganttDependenciesColumnHeader") || defaults.ganttDependenciesColumnHeader,
    toolbarZoomInTip: tTreeGrid("toolbarZoomInTip") || defaults.toolbarZoomInTip,
    toolbarZoomOutTip: tTreeGrid("toolbarZoomOutTip") || defaults.toolbarZoomOutTip,
    toolbarZoomFitTip: tTreeGrid("toolbarZoomFitTip") || defaults.toolbarZoomFitTip,
    toolbarPrevTip: tTreeGrid("toolbarPrevTip") || defaults.toolbarPrevTip,
    toolbarNextTip: tTreeGrid("toolbarNextTip") || defaults.toolbarNextTip,
    toolbarAddTaskBtnLabel: tTreeGrid("toolbarAddTaskBtnLabel") || defaults.toolbarAddTaskBtnLabel,
    archiveTask: tTreeGrid("archiveTask") || defaults.archiveTask,
    deleteDependency: tTreeGrid("deleteDependency") || defaults.deleteDependency,
    changeDependencyLag: tTreeGrid("changeDependencyLag") || defaults.changeDependencyLag,
    deleteMainBar: tTreeGrid("deleteMainBar") || defaults.deleteMainBar,
    taskArchiveSuccessMessage: tTreeGrid("taskArchiveSuccessMessage") || defaults.taskArchiveSuccessMessage,
    taskArchiveErrorMessage: tTreeGrid("taskArchiveErrorMessage") || defaults.taskArchiveErrorMessage,
    rowHeaders: tTreeGrid("rowHeaders") || defaults.rowHeaders,
    pageHeader: tTreeGrid("pageHeader") || defaults.pageHeader,
    addTask: tTreeGrid("addTask") || defaults.addTask,
    addTaskAbove: tTreeGrid("addTaskAbove") || defaults.addTaskAbove,
    addTaskBelow: tTreeGrid("addTaskBelow") || defaults.addTaskBelow,
    addSubtask: tTreeGrid("addSubtask") || defaults.addSubtask,
    copyTaskAbove: tTreeGrid("copyTaskAbove") || defaults.copyTaskAbove,
    copyTaskBelow: tTreeGrid("copyTaskBelow") || defaults.copyTaskBelow,
    copyTaskTreeAbove: tTreeGrid("copyTaskTreeAbove") || defaults.copyTaskTreeAbove,
    copyTaskTreeBelow: tTreeGrid("copyTaskTreeBelow") || defaults.copyTaskTreeBelow,
    contextMenuHelpModalTitle: tTreeGrid("contextMenuHelpModalTitle") || defaults.contextMenuHelpModalTitle,
    contextMenuHelpModalAlertInfo: tTreeGrid("contextMenuHelpModalAlertInfo") || defaults.contextMenuHelpModalAlertInfo,
    contextMenuHelpModalCheckboxLabel:
      tTreeGrid("contextMenuHelpModalCheckboxLabel") || defaults.contextMenuHelpModalCheckboxLabel,
    noData: tTreeGrid("noData") || defaults.noData,
    dependenciesLabel: tTreeGrid("dependenciesLabel") || defaults.dependenciesLabel,
    ganttChartLabel: tTreeGrid("ganttChartLabel") || defaults.ganttChartLabel,
    chooseColumnsLabel: tTreeGrid("chooseColumnsLabel") || defaults.chooseColumnsLabel,
    chooseRowsLabel: tTreeGrid("chooseRowsLabel") || defaults.chooseRowsLabel,
    chooseItemsLabel: tTreeGrid("chooseItemsLabel") || defaults.chooseItemsLabel,
    rowHeadersLabel: tTreeGrid("rowHeadersLabel") || defaults.rowHeadersLabel,
    chooseToolbarsLabel: tTreeGrid("chooseToolbarsLabel") || defaults.chooseToolbarsLabel,
    printOptionsLabel: tTreeGrid("printOptionsLabel") || defaults.printOptionsLabel,
    printMaxRowsPerPageLabel: tTreeGrid("printMaxRowsPerPageLabel") || defaults.printMaxRowsPerPageLabel,
    printStartRowLabel: tTreeGrid("printStartRowLabel") || defaults.printStartRowLabel,
    printExpandedRowsLabel: tTreeGrid("printExpandedRowsLabel") || defaults.printExpandedRowsLabel,
    printVisibleGantLabel: tTreeGrid("printVisibleGantLabel") || defaults.printVisibleGantLabel,
    printPageSizeLabel: tTreeGrid("printPageSizeLabel") || defaults.printPageSizeLabel,
    printBrowserPrintInfoLabel: tTreeGrid("printBrowserPrintInfoLabel") || defaults.printBrowserPrintInfoLabel,
    printPageOrientationLabel: tTreeGrid("printPageOrientationLabel") || defaults.printPageOrientationLabel,
    printMarginWidthLabel: tTreeGrid("printMarginWidthLabel") || defaults.printMarginWidthLabel,
    printMarginHeightLabel: tTreeGrid("printMarginHeightLabel") || defaults.printMarginHeightLabel,
    printFitToPageLabel: tTreeGrid("printFitToPageLabel") || defaults.printFitToPageLabel,
    printDPIRatioLabel: tTreeGrid("printDPIRatioLabel") || defaults.printDPIRatioLabel,
    printPageWidthLabel: tTreeGrid("printPageWidthLabel") || defaults.printPageWidthLabel,
    printPageHeightLabel: tTreeGrid("printPageHeightLabel") || defaults.printPageHeightLabel,
    printSizeInfo: tTreeGrid("printSizeInfo") || defaults.printSizeInfo,
    landscapeLabel: tTreeGrid("landscapeLabel") || defaults.landscapeLabel,
    portraitLabel: tTreeGrid("portraitLabel") || defaults.portraitLabel,
    widthLabel: tTreeGrid("widthLabel") || defaults.widthLabel,
    heightLabel: tTreeGrid("heightLabel") || defaults.heightLabel,
    singlePageLabel: tTreeGrid("singlePageLabel") || defaults.singlePageLabel,
    noneLabel: tTreeGrid("noneLabel") || defaults.noneLabel,
    exportOptionsLabel: tTreeGrid("exportOptionsLabel") || defaults.exportOptionsLabel,
    exportFileNameLabel: tTreeGrid("exportFileNameLabel") || defaults.exportFileNameLabel,
    exportFileFormatLabel: tTreeGrid("exportFileFormatLabel") || defaults.exportFileFormatLabel,
    exportAllRowsExpandedLabel: tTreeGrid("exportAllRowsExpandedLabel") || defaults.exportAllRowsExpandedLabel,
    exportTreeInExcelLabel: tTreeGrid("exportTreeInExcelLabel") || defaults.exportTreeInExcelLabel,
    exportIndentTreeInMainColumnLabel:
      tTreeGrid("exportIndentTreeInMainColumnLabel") || defaults.exportIndentTreeInMainColumnLabel,
    exportFreezeHeadRowsLabel: tTreeGrid("exportFreezeHeadRowsLabel") || defaults.exportFreezeHeadRowsLabel,
    exportFreezeLeftColumnsLabel: tTreeGrid("exportFreezeLeftColumnsLabel") || defaults.exportFreezeLeftColumnsLabel,
    exportEmptyCellsLabel: tTreeGrid("exportEmptyCellsLabel") || defaults.exportEmptyCellsLabel,
    scaleLabel: tTreeGrid("scaleLabel") || defaults.scaleLabel,
    filterOffLabel: tTreeGrid("filterOffLabel") || defaults.filterOffLabel,
    filterEqualLabel: tTreeGrid("filterEqualLabel") || defaults.filterEqualLabel,
    filterNotEqualLabel: tTreeGrid("filterNotEqualLabel") || defaults.filterNotEqualLabel,
    filterLessThanLabel: tTreeGrid("filterLessThanLabel") || defaults.filterLessThanLabel,
    filterLessThanOrEqualLabel: tTreeGrid("filterLessThanOrEqualLabel") || defaults.filterLessThanOrEqualLabel,
    filterGreaterThanLabel: tTreeGrid("filterGreaterThanLabel") || defaults.filterGreaterThanLabel,
    filterGreaterThanOrEqualLabel: tTreeGrid("filterGreaterThanOrEqualLabel") || defaults.filterGreaterThanOrEqualLabel,
    filterBeginsWithLabel: tTreeGrid("filterBeginsWithLabel") || defaults.filterBeginsWithLabel,
    filterDoesnotBeginWithLabel: tTreeGrid("filterDoesnotBeginWithLabel") || defaults.filterDoesnotBeginWithLabel,
    filterEndsWithLabel: tTreeGrid("filterEndsWithLabel") || defaults.filterEndsWithLabel,
    filterDoesnotEndWithLabel: tTreeGrid("filterDoesnotEndWithLabel") || defaults.filterDoesnotEndWithLabel,
    filterContainsLabel: tTreeGrid("filterContainsLabel") || defaults.filterContainsLabel,
    filterDoesnotContainLabel: tTreeGrid("filterDoesnotContainLabel") || defaults.filterDoesnotContainLabel,
    Aqua: tTreeGrid("Aqua") || defaults.Aqua,
    Black: tTreeGrid("Black") || defaults.Black,
    Blue: tTreeGrid("Blue") || defaults.Blue,
    Fuchsia: tTreeGrid("Fuchsia") || defaults.Fuchsia,
    Gray: tTreeGrid("Gray") || defaults.Gray,
    Green: tTreeGrid("Green") || defaults.Green,
    Lime: tTreeGrid("Lime") || defaults.Lime,
    Maroon: tTreeGrid("Maroon") || defaults.Maroon,
    Navy: tTreeGrid("Navy") || defaults.Navy,
    Olive: tTreeGrid("Olive") || defaults.Olive,
    Orange: tTreeGrid("Orange") || defaults.Orange,
    Purple: tTreeGrid("Purple") || defaults.Purple,
    Red: tTreeGrid("Red") || defaults.Red,
    Silver: tTreeGrid("Silver") || defaults.Silver,
    Teal: tTreeGrid("Teal") || defaults.Teal,
    White: tTreeGrid("White") || defaults.White,
    Yellow: tTreeGrid("Yellow") || defaults.Yellow,
    noGrouping: tTreeGrid("noGrouping") || defaults.noGrouping,
    groupByManagers: tTreeGrid("groupByManagers") || defaults.groupByManagers,
    groupByResponsibles: tTreeGrid("groupByResponsibles") || defaults.groupByResponsibles,
    groupByParticipants: tTreeGrid("groupByParticipants") || defaults.groupByParticipants,
    groupByCustomers: tTreeGrid("groupByCustomers") || defaults.groupByCustomers,
    groupBySuppliers: tTreeGrid("groupBySuppliers") || defaults.groupBySuppliers,
    groupByWorkspaces: tTreeGrid("groupByWorkspaces") || defaults.groupByWorkspaces,
    deleteRow: tTreeGrid("deleteRow") || defaults.deleteRow,
    clear: tTreeGrid("clear") || defaults.clear,
    nameColumnHeader: tTreeGrid("nameColumnHeader") || defaults.nameColumnHeader,
    fromColumnHeader: tTreeGrid("fromColumnHeader") || defaults.fromColumnHeader,
    toColumnHeader: tTreeGrid("toColumnHeader") || defaults.toColumnHeader,
    isRecurringColumnHeader: tTreeGrid("isRecurringColumnHeader") || defaults.isRecurringColumnHeader,
    addHolidayButtonLabel: tTreeGrid("addHolidayButtonLabel") || defaults.addHolidayButtonLabel,
    gotIt: tTreeGrid("gotIt") || defaults.gotIt,
    firstTaskAdded: tTreeGrid("firstTaskAdded") || defaults.firstTaskAdded,
    startDateAfterEndDateError: tTreeGrid("startDateAfterEndDateError") || defaults.startDateAfterEndDateError,
  }
}

export const defaultTranslations = {
  rowNumberColumnHeader: "No.",
  taskNumberShortColumnHeader: "TNo.",
  projectNumberShortColumnHeader: "PNo.",
  titleColumnHeader: "Title",
  descriptionColumnHeader: "Description",
  plannedStartDateColumnHeader: "Planned start",
  plannedEndDateColumnHeader: "Planned end",
  startDateAfterEndDateError: "The planned start date cannot be after the planned end date.",
  actualStartDateColumnHeader: "Actual start",
  actualEndDateColumnHeader: "Actual end",
  statusColumnHeader: "Status",
  statusDescriptionColumnHeader: "Status description",
  stateColumnHeader: "State",
  dueDateColumnHeader: "Due date",
  customersColumnHeader: "Customer",
  suppliersColumnHeader: "Suppliers",
  workspacesColumnHeader: "Workspaces",
  responsibleColumnHeader: "Responsible",
  managersColumnHeader: "Manager",
  participantsColumnHeader: "Participants",
  completedColumnHeader: "Done",
  rejectedDateColumnHeader: "Rejected",
  createdAtColumnHeader: "Created at",
  creatorColumnHeader: "Creator name",
  templateTitleColumnHeader: "Template name",
  toolbarSearchInputLabel: "Search",
  toolbarSearchInputTip: "Type to search",
  toolbarPrintTip: "Print",
  toolbarExportTip: "Export",
  toolbarExpandAllTip: "Expand all",
  toolbarCollapseAllTip: "Collapse all",
  toolbarColumnsTip: "Display column chooser",
  toolbarFilterTip: "Show or hide filter row",
  toolbarTodoListSummaryText: `<p>Total: <b> {{rowCount}} </b> rows`,
  toolbarActivitiesListSummaryText: `<p>Total: <b> {{rowCount}} </b> rows`,
  toolbarProjectListSummaryText: `<p>Total: <b> {{rowCount}} </b> rows`,
  toolbarTaskListSummaryText: `<p>Total: <b> {{rowCount}} </b> rows`,
  pageHeaderRowLabel: "Page header",
  headerRowLabel: "Row headers",
  statusFilterOffLabel: "All statuses",
  managersFilterOffLabel: "All managers",
  participantsFilterOffLabel: "All participants",
  suppliersFilterOffLabel: "All suppliers",
  workspacesFilterOffLabel: "All workspaces",
  stateFilterOffLabel: "All states",
  customersFilterOffLabel: "All customers",
  responsibleFilterOffLabel: "All responsible",
  addTodoButtonLabel: "Add to-do",
  showTimeColumnHeader: "Show time",
  actionsColumnHeader: "Actions",
  markTodoAsCompletedTip: "Mark to-do as completed",
  markTodoAsNotCompletedTip: "Mark to-do as not completed",
  notReported: "Not reported",
  activitiesListProjectGroupHeader: "Projects",
  activitiesListTaskTodoAndCalendarEventGroupHeader: "Tasks, to-dos and calendar events",
  addProjectButtonLabel: "Add project",
  zoomMenuHoursAndQuarters: "Hours and Quarters",
  zoomMenuDaysAndHours: "Days and Hours",
  zoomMenuWeeksAndHalfDays: "Weeks and Half Days",
  zoomMenuWeeksAndDays: "Weeks and Days",
  zoomMenuMonthsAndWeeks: "Months and Weeks",
  zoomMenuMonthsWeeksAndDays: "Months, Weeks and Days",
  zoomMenuQuartersAndMonths: "Quarters and Months",
  zoomMenuHalvesAndMonths: "Halves and Months",
  zoomMenuYearsAndQuarters: "Years and Quarters",
  openColumnHeader: "",
  taskNumberColumnHeader: "Task number",
  durationColumnHeader: "Duration",
  daysLeftColumnHeader: "Days left",
  basePlanStartDateColumnHeader: "Base plan start",
  basePlanEndDateColumnHeader: "Base plan end",
  ganttDependenciesColumnHeader: "Gantt ancestors",
  toolbarZoomInTip: "Zoom in",
  toolbarZoomOutTip: "Zoom out",
  toolbarZoomFitTip: "Zoom to fit",
  toolbarPrevTip: "Scroll gantt backward",
  toolbarNextTip: "Scroll gantt forward",
  toolbarAddTaskBtnLabel: "Add task",
  archiveTask: "Archive task",
  deleteDependency: "Delete dependency",
  changeDependencyLag: "Change dependency's lag",
  deleteMainBar: "Delete main bar",
  taskArchiveSuccessMessage: "Task archived successfully",
  taskArchiveErrorMessage: "Failed to archive task",
  rowHeaders: "Row headers",
  pageHeader: "Page Header",
  addTask: "Add task",
  addTaskAbove: "Add task above",
  addTaskBelow: "Add task below",
  addSubtask: "Add subtask",
  copyTaskAbove: "Copy task above",
  copyTaskBelow: "Copy task below",
  copyTaskTreeAbove: "Copy task tree above",
  copyTaskTreeBelow: "Copy task tree below",
  contextMenuHelpModalTitle: "You can create tasks by right clicking on any row.",
  contextMenuHelpModalAlertInfo: "Watch the demo below to see how it works.",
  contextMenuHelpModalCheckboxLabel: "Don't show me this again",
  notStarted: "Not started",
  inProgress: "In progress",
  completed: "Completed",
  rejected: "Rejected",

  noData: "No data found",
  dependenciesLabel: "Dependencies",
  ganttChartLabel: "Gantt chart",
  chooseColumnsLabel: "Choose columns",
  chooseRowsLabel: "Choose rows",
  chooseItemsLabel: "Choose items",
  rowHeadersLabel: "Row headers",
  chooseToolbarsLabel: "Choose toolbars",
  printOptionsLabel: "Print options",
  printMaxRowsPerPageLabel: "Max rows per page",
  printStartRowLabel: "Start page always by root row",
  printExpandedRowsLabel: "Print all rows expanded",
  printVisibleGantLabel: "Print visible Gantt only",
  printPageSizeLabel: "Page size",
  printBrowserPrintInfoLabel: "Set the same values also in the browser print dialog!",
  printPageOrientationLabel: "Page orientation",
  printMarginWidthLabel: "Page margin width (mm)",
  printMarginHeightLabel: "Page margin height (mm)",
  printFitToPageLabel: "Fit to one page",
  printDPIRatioLabel: "DPI ratio",
  printPageWidthLabel: "Page width (px)",
  printPageHeightLabel: "Page height (px)",
  printSizeInfo: "Page size is 1 x 1 pages",
  landscapeLabel: "Landscape",
  portraitLabel: "Portrait",
  widthLabel: "Width",
  heightLabel: "Height",
  singlePageLabel: "Single page",
  noneLabel: "None",
  exportOptionsLabel: "Export options",
  exportFileNameLabel: "File name",
  exportFileFormatLabel: "File format",
  exportAllRowsExpandedLabel: "Export all rows expanded",
  exportTreeInExcelLabel: "Export tree in Excel outline",
  exportIndentTreeInMainColumnLabel: "Indent tree in main column",
  exportFreezeHeadRowsLabel: "Freeze (anchor) head rows",
  exportFreezeLeftColumnsLabel: "Freeze (anchor) left columns",
  exportEmptyCellsLabel: "Export also empty cells",
  scaleLabel: "Scale",
  okButtonLabel: "OK",
  cancelButtonLabel: "Cancel",
  hideAllButtonLabel: "Hide all",
  allOnButtonLabel: "All on",
  clear: "Clear",
  longDayNames: "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday",
  shortDayNames: "Sun,Mon,Tue,Wed,Thu,Fri,Sat",
  day2CharNames: "Su,Mo,Tu,We,Th,Fr,Sa",
  day1CharNames: "S,M,T,W,T,F,S",
  longMonthNames: "January,February,March,April,May,June,July,August,September,October,November,December",
  longMonthNames2: "January,February,March,April,May,June,July,August,September,October,November,December",
  shortMonthNames: "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",
  shortWeekLabel: "wk",
  showLabel: "Show",

  filterOffLabel: "Off",
  filterEqualLabel: "Equal",
  filterNotEqualLabel: "Not equal",
  filterLessThanLabel: "Less than",
  filterLessThanOrEqualLabel: "Less than or equal",
  filterGreaterThanLabel: "Greater than",
  filterGreaterThanOrEqualLabel: "Greater than or equal",
  filterBeginsWithLabel: "Begins with",
  filterDoesnotBeginWithLabel: "Does not begin with",
  filterEndsWithLabel: "Ends with",
  filterDoesnotEndWithLabel: "Does not end with",
  filterContainsLabel: "Contains",
  filterDoesnotContainLabel: "Does not contain",

  color: "Color",
  Default: "Default",
  Aqua: "Aqua",
  Black: "Black",
  Blue: "Blue",
  Fuchsia: "Fuchsia",
  Gray: "Gray",
  Green: "Green",
  Lime: "Lime",
  Maroon: "Maroon",
  Navy: "Navy",
  Olive: "Olive",
  Orange: "Orange",
  Purple: "Purple",
  Red: "Red",
  Silver: "Silver",
  Teal: "Teal",
  White: "White",
  Yellow: "Yellow",

  noGrouping: "No grouping",
  groupByManagers: "Group by Managers",
  groupByResponsibles: "Group by Responsibles",
  groupByParticipants: "Group by Participants",
  groupByCustomers: "Group by Customers",
  groupBySuppliers: "Group by Suppliers",
  groupByWorkspaces: "Group by Workspaces",
  linksColumnHeader: "Links",
  linksAndAttachmentsColumnHeader: "Links and attachments",
  deleteRow: "Delete row",

  nameColumnHeader: "Name",
  fromColumnHeader: "From",
  toColumnHeader: "To",
  isRecurringColumnHeader: "Recurring",
  addHolidayButtonLabel: "Add holiday",

  completionColumnHeader: "Completion",
  completionBasedOnTasksColumnHeader: "Task-based completion",
  showMenu: "Show menu",
  gotIt: "Got it!",
  firstTaskAdded: "Success! First Task Added",
}

export type TreeGridTranslations = typeof defaultTranslations
